import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ArrowDownRight, ArrowUpRight, ChevronRight, Minus } from "lucide-react";
import { api } from "../lib/api";
import type { Overview as OverviewData } from "../lib/types";
import { money, number, percent } from "../lib/format";

const PERIODS = [
  { days: 7, label: "7 d" },
  { days: 30, label: "30 d" },
  { days: 90, label: "90 d" },
  { days: 365, label: "12 mån" },
];

const SEVERITY = {
  danger: "border-red-900/60 bg-red-950/30 text-red-300",
  warning: "border-amber-900/60 bg-amber-950/30 text-amber-300",
  info: "border-sky-900/60 bg-sky-950/30 text-sky-300",
};

export default function Overview() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery({
    queryKey: ["overview", days],
    queryFn: () => api.get<OverviewData>("/superadmin/overview", { days }),
    refetchInterval: 60_000,
  });

  const chartData = data
    ? data.series.labels.map((label, i) => {
        const point: Record<string, string | number> = { label: label.slice(5) };
        for (const set of data.series.datasets) point[set.key] = set.data[i];
        return point;
      })
    : [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Översikt</h1>
          <p className="text-sm text-ink-500">Marknadsplatsens puls de senaste {days} dagarna</p>
        </div>
        <div className="flex rounded-md border border-ink-700 p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                days === p.days ? "bg-ink-700 text-white" : "text-ink-400 hover:text-ink-200"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {data && data.alerts.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {data.alerts.map((alert) => {
            const body = (
              <div className={`flex items-center justify-between rounded-lg border px-3 py-2.5 text-sm ${SEVERITY[alert.severity]}`}>
                <span className="pr-3">{alert.label}</span>
                <span className="flex shrink-0 items-center gap-1 font-semibold num">
                  {alert.count}
                  {alert.resource && <ChevronRight className="h-3.5 w-3.5 opacity-60" />}
                </span>
              </div>
            );
            if (!alert.resource) return <div key={alert.label}>{body}</div>;
            const query = new URLSearchParams(
              Object.entries(alert.filters).map(([k, v]) => [`filters[${k}]`, v]),
            ).toString();
            return (
              <Link key={alert.label} to={`/r/${alert.resource}${query ? `?${query}` : ""}`}>
                {body}
              </Link>
            );
          })}
        </div>
      )}

      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading
          ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="panel h-[86px] animate-pulse" />)
          : data?.kpis.map((kpi) => {
              const format = (n: number) => (kpi.format === "money" ? money(n) : number(n));
              // change is null when the previous period was zero — a "+∞%" is
              // noise, so say there is nothing to compare against instead.
              const comparable = kpi.change !== null;
              const up = (kpi.change ?? 0) > 0;
              const flat = kpi.change === 0;
              const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
              const tone = flat ? "text-ink-600" : up ? "text-emerald-400" : "text-red-400";

              return (
                <div key={kpi.label} className="panel p-3.5">
                  <div className="text-xs text-ink-500">{kpi.label}</div>
                  <div className="mt-1 text-2xl font-semibold text-white num">{format(kpi.value)}</div>
                  <div className="mt-1 flex items-center gap-1 text-xs">
                    {comparable ? (
                      <>
                        <Icon className={`h-3 w-3 ${tone}`} />
                        <span className={tone}>{percent(kpi.change)}</span>
                        <span className="text-ink-600">mot {format(kpi.previous)}</span>
                      </>
                    ) : (
                      <span className="text-ink-600">Inget att jämföra med</span>
                    )}
                  </div>
                </div>
              );
            })}
      </div>

      <div className="panel p-4">
        <h2 className="mb-3 text-sm font-semibold text-white">Volym per dag</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
              <defs>
                {["bookings", "car_care_bookings", "requests", "users"].map((key, i) => (
                  <linearGradient key={key} id={`g-${key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SERIES_COLORS[i]} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={SERIES_COLORS[i]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1c2534" vertical={false} />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#151d2b", border: "1px solid #2a3547", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#cbd5e1" }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              {["bookings", "car_care_bookings", "requests", "users"].map((key, i) => (
                <Area
                  key={key} type="monotone" dataKey={key}
                  name={data?.series.datasets.find((d) => d.key === key)?.label || key}
                  stroke={SERIES_COLORS[i]} fill={`url(#g-${key})`} strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="panel p-4">
          <h2 className="mb-3 text-sm font-semibold text-white">Omsättning per dag</h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="g-revenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34d399" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1c2534" vertical={false} />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} minTickGap={24} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={54} />
                <Tooltip
                  formatter={(value: number) => money(value)}
                  contentStyle={{ background: "#151d2b", border: "1px solid #2a3547", borderRadius: 8, fontSize: 12 }}
                />
                <Area type="monotone" dataKey="revenue" name="Omsättning" stroke="#34d399" fill="url(#g-revenue)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="panel p-4">
          <h2 className="mb-3 text-sm font-semibold text-white">Bokningsstatus</h2>
          <Distribution data={data?.breakdowns.booking_status} />
          <h2 className="mb-3 mt-5 text-sm font-semibold text-white">Betalmetod</h2>
          <Distribution data={data?.breakdowns.payment_method} />
        </div>

        <div className="panel p-4">
          <h2 className="mb-3 text-sm font-semibold text-white">Toppverkstäder</h2>
          <div className="space-y-1.5">
            {(data?.breakdowns.top_garages || []).slice(0, 8).map((garage, i) => (
              <Link
                key={garage.id} to={`/r/garages/${garage.id}`}
                className="flex items-center justify-between rounded px-1.5 py-1 text-sm hover:bg-ink-850"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="w-4 shrink-0 text-xs text-ink-600 num">{i + 1}</span>
                  <span className="truncate text-ink-300">{garage.name}</span>
                </span>
                <span className="shrink-0 pl-2 text-xs num">
                  <span className="text-emerald-400">{money(garage.revenue)}</span>
                  <span className="text-ink-600"> · {garage.bookings}</span>
                </span>
              </Link>
            ))}
            {data && data.breakdowns.top_garages.length === 0 && (
              <p className="text-sm text-ink-600">Inga bokningar i perioden.</p>
            )}
          </div>

          <h2 className="mb-2 mt-5 text-sm font-semibold text-white">Utbud</h2>
          <dl className="space-y-1 text-sm">
            {Object.entries(data?.breakdowns.supply || {}).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <dt className="text-ink-500">{SUPPLY_LABELS[key] || key}</dt>
                <dd className="num text-ink-200">{number(value)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

const SERIES_COLORS = ["#38bdf8", "#a78bfa", "#fbbf24", "#34d399"];

const SUPPLY_LABELS: Record<string, string> = {
  garages_active: "Aktiva verkstäder",
  garages_total: "Verkstäder totalt",
  centers_active: "Aktiva bilvårdscenter",
  centers_total: "Bilvårdscenter totalt",
  stations: "Besiktningsstationer",
};

function Distribution({ data }: { data?: Record<string, number> }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  if (total === 0) return <p className="text-sm text-ink-600">Inga data i perioden.</p>;

  return (
    <div className="space-y-1.5">
      {entries.map(([key, value]) => (
        <div key={key}>
          <div className="flex justify-between text-xs">
            <span className="text-ink-400">{key}</span>
            <span className="num text-ink-300">
              {value} <span className="text-ink-600">({Math.round((value / total) * 100)}%)</span>
            </span>
          </div>
          <div className="mt-0.5 h-1 overflow-hidden rounded bg-ink-800">
            <div className="h-full rounded bg-accent" style={{ width: `${(value / total) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}
